import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress
} from '@mui/material'
import QuotationResults from './QuotationResults'

const BENEFIT_TYPES = [
  'funeral',
  'death',
  'hospital',
  'outpatient',
  'maternity',
  'personal_accident',
  'property',
  'income_loss',
  'cashback',
  'fixed_indemnity',
  'variable_indemnity',
  'medical_expense',
  'legal_expenses',
  'grocery',
  'education',
  'disability',
  'motor',
  'credit_life',
  'travel'
]

const todayISO = () => new Date().toISOString().split('T')[0]

function CompareQuotationDialog(props) {
  const {
    open,
    onClose,
    quotationResults,
    quotationLoader,
    quotationError,
    onCompare
  } = props

  const [benefitType, setBenefitType] = useState('')
  const [scope, setScope] = useState('partner')
  const [dob, setDob] = useState('')
  const [evaluationDate, setEvaluationDate] = useState(todayISO())

  useEffect(() => {
    if (open) {
      setEvaluationDate(todayISO())
      setBenefitType('')
      setScope('partner')
      setDob('')
    }
  }, [open])

  const handleSubmit = () => {
    onCompare({
      benefit_type: benefitType,
      scope,
      dependants: [
        {
          relationship: 'Principal',
          date_of_birth: dob
        }
      ],
      evaluation_date: evaluationDate,
      facts: {}
    })
  }

  const canSubmit = benefitType && dob

  const handleClose = () => {
    onClose()
    setBenefitType('')
    setScope('partner')
    setDob('')
  }

  if (quotationResults) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <QuotationResults results={quotationResults} onClose={handleClose} />
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#173E4B', color: '#fff' }}>
        Compare Quotations
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
          Compare products across the organisation
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Benefit Type</InputLabel>
              <Select
                value={benefitType}
                onChange={e => setBenefitType(e.target.value)}
                label="Benefit Type"
              >
                {BENEFIT_TYPES.map(bt => (
                  <MenuItem key={bt} value={bt}>
                    {bt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Evaluation Date"
              type="date"
              value={evaluationDate}
              onChange={e => setEvaluationDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Scope
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={scope}
              onChange={e => setScope(e.target.value)}
            >
              <FormControlLabel
                value="partner"
                control={
                  <Radio
                    sx={{
                      color: '#173E4B',
                      '&.Mui-checked': { color: '#173E4B' }
                    }}
                  />
                }
                label="Partner"
              />
              <FormControlLabel
                value="organisation"
                control={
                  <Radio
                    sx={{
                      color: '#173E4B',
                      '&.Mui-checked': { color: '#173E4B' }
                    }}
                  />
                }
                label="Organisation"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Dependant
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Relationship"
                value="Principal"
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                type="date"
                value={dob}
                onChange={e => setDob(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Box>

        {quotationError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {quotationError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ color: '#666' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || quotationLoader}
          variant="contained"
          sx={{
            bgcolor: '#173E4B',
            '&:hover': { bgcolor: '#0f2d38' },
            '&:disabled': { bgcolor: '#ccc' }
          }}
          startIcon={
            quotationLoader ? (
              <CircularProgress size={20} sx={{ color: '#fff' }} />
            ) : null
          }
        >
          {quotationLoader ? 'Comparing...' : 'Compare'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompareQuotationDialog
